/**
 * rebuy-translations.js — v1
 *
 * Applies locale translations to Rebuy Smart Cart and recommendation widgets.
 *
 * Structure:
 *  - Single readiness gate (`ready`). Rebuy.SmartCart is read ONLY after
 *    `ready` resolves, i.e. after the Smart Cart SDK has initialised and the
 *    Storefront Helper (loaded async) is available. No code path reads
 *    Rebuy.SmartCart.settings before that.
 *  - All functions are local to this IIFE (no implicit globals).
 *  - DOM-only updates do not depend on the SDK.
 */
(() => {
  "use strict";

  const rebuyLocale = window.rebuyLocale;
  if (!rebuyLocale) return; // inline head script did not run (translations disabled / primary locale)

  const SMART_CART_TIMEOUT_MS = 10000;
  const locale = Shopify?.locale;
  const translations = { [locale]: rebuyLocale };

  /* ------------------------------------------------------------------ *
   * Readiness gate
   * ------------------------------------------------------------------ */

  // Resolves when window.Rebuy.SmartCart.settings exists. Covers both
  // "already initialised before this script ran" and "event fires later".
  const smartCartReady = new Promise((resolve) => {
    if (window.Rebuy?.SmartCart?.settings) {
      resolve();
      return;
    }

    const onReady = () => {
      clearTimeout(timer);
      resolve();
    };

    document.addEventListener("rebuy:smartcart.ready", onReady, { once: true });

    const timer = setTimeout(() => {
      document.removeEventListener("rebuy:smartcart.ready", onReady);
      console.warn(
        "[rebuy-translations] Smart Cart not ready after " +
          SMART_CART_TIMEOUT_MS +
          " ms; translations skipped."
      );
    }, SMART_CART_TIMEOUT_MS);
  });

  // storefront-helper.js is loaded with `async`, so it may arrive after this
  // script. Deferred scripts run before window "load", so waiting for "load"
  // is safe when the helper is not yet defined.
  const helperReady = new Promise((resolve) => {
    if (typeof window.ShopifyStorefrontHelper === "function") {
      resolve();
    } else {
      window.addEventListener("load", () => resolve(), { once: true });
    }
  });

  const ready = Promise.all([smartCartReady, helperReady]).then(() => {
    if (
      !window.Rebuy?.SmartCart?.settings ||
      typeof window.ShopifyStorefrontHelper !== "function"
    ) {
      console.warn(
        "[rebuy-translations] Dependencies missing after load; translations skipped."
      );
      return false;
    }
    return true;
  });

  // Runs fn only when the SDK and helper are confirmed available.
  const whenReady = (fn) =>
    ready.then((ok) => {
      if (ok) fn();
    });

  /* ------------------------------------------------------------------ *
   * Smart Cart translations (requires SDK)
   * ------------------------------------------------------------------ */

  const updateSmartCartLanguage = () => {
    const settings = window.Rebuy.SmartCart.settings;
    const translation = translations[locale]?.smart_cart;
    const subscriptionTranslations =
      translations[locale]?.cart_switch_to_subscription;
    const tierProgressBarTranslations = translations[locale]?.tier_progress_bar;
    const components = settings.components;
    const buyMoreSaveMore = settings.buy_more_save_more;
    const emptyCart = settings.empty_cart_markup;
    const switchToSubscription = settings.switch_to_subscription;
    const storefrontHelper = new window.ShopifyStorefrontHelper();

    if (!(locale && translation && components)) return;

    components.forEach((component) => {
      switch (component.type) {
        case "checkout": {
          Object.assign(component.settings.language, {
            checkout_label: translation.checkout_label,
            view_cart_label: translation.view_cart_label,
            checking_out_label: translation.checking_out_label,
            view_cart_working_label: translation.view_cart_working_label,
            continue_shopping_label: translation.continue_shopping_label,
            terms_and_conditions_label: translation.terms_and_conditions_label,
          });
          break;
        }
        case "cart_subtotal": {
          Object.assign(component.settings, {
            subtotal_plural_text: translation.subtotal_plural_text,
            subtotal_singular_text: translation.subtotal_singular_text,
          });
          break;
        }
        case "tiered_progress_bar": {
          const progressBarTranslation = {
            free_shipping_reached:
              tierProgressBarTranslations?.free_shipping_reached,
            free_shipping_remaining:
              tierProgressBarTranslations?.free_shipping_remaining,
            product_amount_reached_label_text:
              tierProgressBarTranslations?.product_amount_reached_label_text,
            product_amount_remaining_label_text:
              tierProgressBarTranslations?.product_amount_remaining_label_text,
            discount_amount_remaining_label_text:
              tierProgressBarTranslations?.discount_amount_remaining_label_text,
            discount_amount_reached_label_text:
              tierProgressBarTranslations?.discount_amount_reached_label_text,
          };

          component.settings.bars?.forEach((bar) => {
            Object.assign(bar.language, progressBarTranslation);
            bar.earnedProducts?.forEach((product) => {
              storefrontHelper.updateProductJSON(product);
            });
            bar.tiers.forEach((tier, index) => {
              if (tier.type !== "shipping") {
                Object.assign(tier, progressBarTranslation);
                if (tier.type === "product") {
                  storefrontHelper.updateProductJSON(tier.products[0]);
                }
              }

              if (tier.type === "shipping") {
                const labels = document.querySelectorAll(
                  ".rebuy-cart__progress-step-label"
                );
                const label = labels?.[index];
                if (label) {
                  label.innerText =
                    tierProgressBarTranslations?.free_shipping || "";
                }
              }
            });
          });
          break;
        }
        case "title_bar": {
          Object.assign(component.settings, { title: translation.cart_title });
          break;
        }
        case "login": {
          Object.assign(component.settings.language, {
            title: translation.login_title,
          });
          break;
        }
        case "discount_code": {
          Object.assign(component.settings.language, {
            discount_code_label: translation.discount_code_label,
            discount_button_label: translation.discount_button_label,
            discount_invalid_message: translation.discount_invalid_message,
          });
          break;
        }
        case "notes": {
          Object.assign(component.settings.language, {
            notes_label: translation.notes_label,
            notes_placeholder: translation.notes_placeholder,
            notes_remaining_characters: translation.notes_remaining_characters,
          });
          break;
        }
      }
    });

    if (buyMoreSaveMore) {
      Object.assign(buyMoreSaveMore.language, {
        bmsm_discount_message: translation.bmsm_discount_message,
        bmsm_discount_success: translation.bmsm_discount_success,
      });
    }

    if (emptyCart) {
      Object.assign(settings, {
        empty_cart_markup: translation.empty_cart_markup,
      });
    }

    if (switchToSubscription && subscriptionTranslations) {
      Object.assign(switchToSubscription.language, {
        downgrading_label: subscriptionTranslations.downgrading_label,
        onetime_option_group_label:
          subscriptionTranslations.onetime_option_group_label,
        onetime_option_label: subscriptionTranslations.onetime_option_label,
        subscription_option_group_label:
          subscriptionTranslations.subscription_option_group_label,
        upgrade_to_subscription_label:
          subscriptionTranslations.upgrade_to_subscription_label,
        upgrade_to_subscription_without_discount_label:
          subscriptionTranslations.upgrade_to_subscription_without_discount_label,
        upgrading_label: subscriptionTranslations.upgrading_label,
      });
    }
  };

  const updateAnnouncementBar = () => {
    const announcementTranslations =
      translations[locale]?.announcement_bar?.bars;
    const components = window.Rebuy.SmartCart.settings.components;
    if (!announcementTranslations || !components) return;

    components
      .filter((c) => c.type === "announcement_bar")
      .forEach((bar, index) => {
        if (announcementTranslations[index]) {
          Object.assign(bar.settings, announcementTranslations[index]);
        }
      });
  };

  /* ------------------------------------------------------------------ *
   * DOM and widget translations (no SDK access needed)
   * ------------------------------------------------------------------ */

  const updateWidgetLanguage = (widget) => {
    const widgetTranslation = translations[locale]?.[widget.id];
    const globalTranslation = translations[locale]?.widget;
    const source = widgetTranslation || globalTranslation;
    if (!locale || !source) return;

    Object.assign(widget.data.settings.language, source);
    widget.compileConfig();
  };

  const updateTierLanguage = () => {
    const smartCartTranslation = translations[locale]?.smart_cart;
    if (!smartCartTranslation) return;

    const giftTitle = document.querySelector(
      ".rebuy-cart__progress-gifts-title"
    );
    if (giftTitle && smartCartTranslation.gift_title) {
      giftTitle.textContent = smartCartTranslation.gift_title;
    }

    const selectGift = smartCartTranslation.select_gift;
    if (!selectGift) return;

    document
      .querySelectorAll(".rebuy-cart__progress-gift-variant-select-prompt")
      .forEach((prompt) => (prompt.textContent = selectGift));

    document
      .querySelectorAll(
        '.rebuy-cart__progress-gift-variant-select option[value=""]'
      )
      .forEach((prompt) => (prompt.textContent = selectGift));
  };

  /* ------------------------------------------------------------------ *
   * Event wiring
   * ------------------------------------------------------------------ */

  // Initial pass: covers the case where Smart Cart was ready before this ran.
  whenReady(() => {
    updateSmartCartLanguage();
    updateAnnouncementBar();
  });

  // Re-apply whenever Smart Cart (re)initialises.
  document.addEventListener("rebuy:smartcart.ready", () => {
    whenReady(() => {
      updateSmartCartLanguage();
      updateAnnouncementBar();
    });
  });

  // Widgets do not depend on Smart Cart.
  document.addEventListener("rebuy.ready", (event) => {
    updateWidgetLanguage(event.detail.widget);
  });

  // Cart changes: let Rebuy finish its DOM update first (setTimeout 0).
  document.addEventListener("rebuy:cart.change", () => {
    setTimeout(() => {
      updateTierLanguage();
      whenReady(() => {
        updateSmartCartLanguage();
        updateAnnouncementBar();
      });
    }, 0);
  });

  // Keep product JSON enriched whenever Rebuy loads or changes products.
  document.addEventListener("rebuy.productsChange", (e) => {
    const widget = e.detail.widget;
    whenReady(() => {
      const storefrontHelper = new window.ShopifyStorefrontHelper();
      widget.data.products.forEach((product) =>
        storefrontHelper.updateProductJSON(product)
      );
    });
  });
})();
